/*
 * SavedByTiers — resolves the correct SavedBy Package Protection price tier for the
 * current cart. SavedBy prices protection in brackets by cart value; the bracket table
 * (with per-tier variant ids) is exposed by the SavedBy app on window.SavedBy.feeTiers.
 * We read it directly so our custom checkbox adds the correct variant — the same one the
 * SavedBy widget would pick — instead of a single hardcoded variant.
 */
window.SavedByTiers = {
  ready() {
    return !!(window.SavedBy && Array.isArray(window.SavedBy.feeTiers) && window.SavedBy.feeTiers.length);
  },
  // All protection variant ids across every tier (used to identify the protection line).
  allVariantIds() {
    return this.ready() ? window.SavedBy.feeTiers.map((t) => String(t.variantId)) : [];
  },
  isProtectionVariant(id) {
    if (id == null) return false;
    return this.allVariantIds().indexOf(String(id)) !== -1;
  },
  // The SavedBy line item in a /cart.js payload, identified by vendor (any tier).
  lineItem(cart) {
    return ((cart && cart.items) || []).find((i) => i.vendor === 'SavedBy');
  },
  /*
   * Lines protection actually covers. It insures a shipment, so anything not shipped —
   * gift cards and any other digital product — is neither covered nor counted towards the
   * price tier. Keyed on requires_shipping rather than a hardcoded gift-card handle, so
   * future digital products need no code change.
   */
  isInsurable(item) {
    return item.vendor !== 'SavedBy' && item.requires_shipping !== false;
  },
  insurableItems(cart) {
    return ((cart && cart.items) || []).filter((i) => this.isInsurable(i));
  },
  hasInsurableItem(cart) {
    return this.insurableItems(cart).length > 0;
  },
  // Cart value to price protection on: shippable lines only, excluding protection itself.
  subtotalCents(cart) {
    return this.insurableItems(cart).reduce((sum, i) => sum + i.final_line_price, 0);
  },
  // The tier {max, price, variantId} for the given cart (first bracket the subtotal fits).
  tierFor(cart) {
    if (!this.ready()) return null;
    const subtotal = this.subtotalCents(cart) / 100;
    const tiers = window.SavedBy.feeTiers;
    for (let i = 0; i < tiers.length; i++) {
      if (subtotal <= tiers[i].max) return tiers[i];
    }
    return tiers[tiers.length - 1];
  },
};

/*
 * Shows the cart's per-line loading state (disabled container + visible line spinners) across
 * every cart line, for any cart mutation — add-to-cart, remove, quantity change, and
 * package-protection add/remove/tier-swap. The cart re-render clears it; callers also clear it
 * explicitly. Only toggles CSS, so it never affects cart/protection request logic.
 */
window.setCartLinesLoading = function (isLoading) {
  const containers = document.querySelectorAll(
    '#CartDrawer-CartItems, #main-cart-items, cart-drawer-items, cart-items'
  );
  containers.forEach((c) => c.classList.toggle('cart__items--disabled', isLoading));

  const spinners = document.querySelectorAll(
    '#CartDrawer-CartItems .loading__spinner, #main-cart-items .loading__spinner, cart-drawer-items .loading__spinner'
  );
  spinners.forEach((s) => s.classList.toggle('hidden', !isLoading));
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setCheckoutButtonHandler();
    // Watch from construction, not from the drawer's transitionend handler — that is
    // registered { once: true } and does not reliably fire, so the observer was never
    // attached and re-renders from the remove path went unnoticed.
    this.observeAtelierSlider();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setCheckoutButtonHandler() {
    // Use event delegation to handle checkout button clicks
    this.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('.cart__checkout-button');
      if (!checkoutButton) return;

      // Check if cart is empty
      if (checkoutButton.disabled) return;

      // Navigate to checkout
      window.location.href = '/checkout';
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        // Initialize atelier slider after cart drawer is fully opened
        this.initAtelierSlider();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // Initialize atelier slider after cart content is updated
      this.initAtelierSlider();

      // Check if protection needs to be added after cart drawer is rendered
      if (autoCheckProtectionHandler && typeof autoCheckProtectionHandler.checkAndAddProtection === 'function') {
        setTimeout(() => {
          autoCheckProtectionHandler.checkAndAddProtection();
        }, 400);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /*
   * Self-heal the slider instead of chasing render sites. The drawer's markup is replaced
   * from several places — updateQuantity, the protection-aware removeItem, renderContents,
   * and the protection tier swap's own onCartUpdate — and any one of them that is not
   * followed by a re-init leaves a fresh .atelier-slider with no Swiper attached, which
   * renders as unstyled markup with dead arrows. Watching the subtree covers every path,
   * including ones added later.
   *
   * This cannot loop: it only acts on a slider that has no .swiper, and initialising one
   * gives it .swiper, so the condition is false on the mutations Swiper itself causes.
   */
  observeAtelierSlider() {
    if (this.atelierObserver) return;
    let pending = null;
    this.atelierObserver = new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(() => {
        const el = this.querySelector('.atelier-slider');
        if (el && !el.swiper) this.initAtelierSlider();
      }, 150);
    });
    this.atelierObserver.observe(this, { childList: true, subtree: true });
  }

  initAtelierSlider() {
    // The slider element must be looked up AFTER the delay, not before it. A cart update
    // replaces the drawer's inner HTML, so an element captured before the wait is detached
    // by the time Swiper runs — Swiper then initialises a node that is no longer on the
    // page and the visible slider is left as unstyled markup with dead arrows.
    setTimeout(() => {
      const atelierSlider = this.querySelector('.atelier-slider');
      if (!atelierSlider) return;

      // Already initialised and still on the page: just remeasure. Tearing it down and
      // rebuilding on every cart update is what makes it flicker and stick.
      if (atelierSlider.swiper) {
        this.atelierSwiper = atelierSlider.swiper;
        this.atelierSwiper.update();
        return;
      }

      // Drop any instance left over from a previous render before building a new one.
      if (this.atelierSwiper) {
        try {
          this.atelierSwiper.destroy(true, true);
        } catch (e) {
          /* already detached by a re-render */
        }
        this.atelierSwiper = null;
      }

      this.atelierSwiper = new Swiper(atelierSlider, {
        slidesPerView: 'auto',
        spaceBetween: 25,
        slidesOffsetBefore: 17,
        slidesOffsetAfter: 17,
        // Scoped to this slider. The previous document-wide selectors bound whichever
        // arrows appeared first in the page, not necessarily this slider's own.
        navigation: {
          nextEl: atelierSlider.querySelector('.swiper-button-next'),
          prevEl: atelierSlider.querySelector('.swiper-button-prev'),
        },
        // The drawer is still animating open when this runs, so the container can measure
        // zero. These make Swiper remeasure itself once it has real layout.
        observer: true,
        observeParents: true,
      });
    }, 100);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateLiveRegions(line, itemCount) {
    super.updateLiveRegions(line, itemCount);
    // Reinitialize atelier slider after cart items are updated
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.initAtelierSlider) {
      setTimeout(() => {
        cartDrawer.initAtelierSlider();
      }, 200);
    }

    // Ensure protection handler is initialized after cart updates
    if (window.initProtectionHandler) {
      window.initProtectionHandler();
    }

    // Ensure auto-check protection handler checks cart after updates
    if (autoCheckProtectionHandler && typeof autoCheckProtectionHandler.checkAndAddProtection === 'function') {
      setTimeout(() => {
        autoCheckProtectionHandler.checkAndAddProtection();
      }, 300);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class ProtectionProductHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleProtectionClick.bind(this));
  }

  setLoadingState(isLoading) {
    // Toggle loading overlay on the cart form
    const cartForm = document.getElementById('CartDrawer-Form');
    if (cartForm) {
      cartForm.classList.toggle('is-loading', isLoading);
    }

    // Disable/enable checkout button
    const checkoutButton = document.querySelector('.cart__checkout-button');
    if (checkoutButton) {
      if (isLoading) {
        checkoutButton.setAttribute('disabled', 'true');
        checkoutButton.dataset.protectionLoading = 'true';
      } else {
        // Only remove disabled if WE set it (not if cart is empty)
        if (checkoutButton.dataset.protectionLoading === 'true') {
          checkoutButton.removeAttribute('disabled');
          delete checkoutButton.dataset.protectionLoading;
        }
      }
    }

    // Also disable the protection checkbox to prevent double-clicks
    const protectionCheckbox = document.querySelector('.protection-checkbox');
    if (protectionCheckbox) {
      protectionCheckbox.disabled = isLoading;
    }

    // And the label (so clicks don't bubble through)
    const protectionLabel = document.querySelector('.cart-checkbox-label');
    if (protectionLabel) {
      protectionLabel.style.pointerEvents = isLoading ? 'none' : '';
    }
  }

  handleProtectionClick(event) {
    const protectionLabel = event.target.closest('.cart-checkbox-label');
    if (!protectionLabel) return;

    const infoIcon = event.target.closest('.info-icon');
    if (infoIcon) return;

    event.preventDefault();

    const checkbox = protectionLabel.querySelector('.protection-checkbox');

    if (!checkbox) return;

    // Toggle checkbox state manually for immediate visual feedback
    checkbox.checked = !checkbox.checked;

    // The variant is resolved inside add/remove (tier for add, vendor line for remove).
    if (checkbox.checked) {
      this.addProtectionProduct(null, checkbox);
    } else {
      this.removeProtectionProduct(null, checkbox);
    }
  }

  /*
   * Single-flight wrapper. The re-entrancy guard in AutoCheckProtectionHandler releases on
   * a timer rather than on completion, so when an add takes longer than that a queued
   * re-check starts a second one: protection ended up in the cart at quantity 2 and the
   * shopper was charged twice. Only one add may be in flight at a time.
   */
  async addProtectionProduct(productId, checkbox) {
    if (this._addInFlight) return;
    this._addInFlight = true;
    try {
      return await this._addProtectionProduct(productId, checkbox);
    } finally {
      this._addInFlight = false;
    }
  }

  async _addProtectionProduct(productId, checkbox) {
    // Show loading state (protection control + all cart lines)
    this.setLoadingState(true);
    window.setCartLinesLoading(true);

    // Clear the "user removed" flag — they're opting back in
    if (autoCheckProtectionHandler) {
      autoCheckProtectionHandler.userRemovedProtection = false;
    }

    // Clear persisted preference — user is opting back in
    sessionStorage.removeItem('protectionUserRemoved');

    // Guard: never add protection unless a real (non-protection) product is in the cart.
    // SavedBy rejects a protection-only cart with 422 "Cannot checkout with only SavedBy".
    // Also resolve the correct price-tier variant for the current cart value.
    let variantToAdd = null;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const hasRealProduct = window.SavedByTiers.hasInsurableItem(cart);
      const alreadyHasProtection = !!window.SavedByTiers.lineItem(cart);
      if (!hasRealProduct || alreadyHasProtection) {
        // Nothing to protect, or protection already present. Revert the checkbox either way.
        if (checkbox) checkbox.checked = alreadyHasProtection;
        // Both loading flags were set on entry, so both must be cleared. Clearing only the
        // control's own state left every cart line stuck behind cart__items--disabled with
        // its spinner showing — prices hidden, atelier row blank, drawer apparently frozen.
        this.setLoadingState(false);
        window.setCartLinesLoading(false);
        // If the shopper actively ticked the box on a cart holding only non-shipped items,
        // silence looks broken — tell them why it did not stick. An empty cart says nothing.
        if (!hasRealProduct && (cart.items || []).length > 0) {
          this.showError('Package protection covers shipped items, so it does not apply to gift cards.');
        }
        return;
      }
      // Resolve the correct tier, waiting briefly for SavedBy's tier table to load if needed.
      let tier = window.SavedByTiers.tierFor(cart);
      for (let i = 0; i < 20 && !tier; i++) {
        await new Promise((r) => setTimeout(r, 100));
        tier = window.SavedByTiers.tierFor(cart);
      }
      if (tier) variantToAdd = tier.variantId;
    } catch (e) {
      /* fall through to the fallback below */
    }
    // No tier resolved and no explicit variant requested: SavedBy's tier table has not
    // loaded yet. Do NOT fall back to the label's base variant here — that is the cheapest
    // bracket ($1.47), so it under-charges on every cart above the first tier, and nothing
    // re-brackets it afterwards because syncProtectionTier bails while tierFor() is null.
    // Skip the add and let recheckWhenTiersReady() do it at the right price. The checkbox
    // must be cleared too, or clickProtectionLabel() sees it checked and refuses to retry.
    if (!variantToAdd && !productId) {
      if (checkbox) checkbox.checked = false;
      this.setLoadingState(false);
      window.setCartLinesLoading(false);
      return;
    }

    // Fallback: the base variant from the label (an HTML attribute that survives AJAX cart
    // re-renders, unlike the inline-script gate var), then the gate var.
    if (!variantToAdd) {
      const label = document.querySelector('.protection-label');
      variantToAdd =
        productId || (label && label.getAttribute('data-base-variant-id')) || window.protectionProductVariantId;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantToAdd,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        // Read the body to detect SavedBy's expected "protection-only" rejection.
        let payload = {};
        try {
          payload = await response.clone().json();
        } catch (e) {
          /* ignore */
        }
        const isProtectionOnlyRejection =
          response.status === 422 &&
          typeof payload.message === 'string' &&
          payload.message.toLowerCase().includes('savedby');

        // Revert the checkbox either way; only surface an error for unexpected failures.
        if (checkbox) checkbox.checked = false;
        if (!isProtectionOnlyRejection) {
          console.error('Error adding protection product to cart:', payload || response.status);
          this.showError('Failed to add protection product. Please try again.');
        }
        return;
      }

      await response.json();

      // Trigger cart update using the existing cart system
      this.updateCartDisplay();
    } catch (error) {
      console.error('Error adding protection product to cart:', error);
      // Revert checkbox state on error
      if (checkbox) checkbox.checked = false;
      this.showError('Failed to add protection product. Please try again.');
    } finally {
      // Hide loading state — wait briefly so the cart drawer re-renders first
      setTimeout(() => {
        this.setLoadingState(false);
        window.setCartLinesLoading(false);
      }, 300);
    }
  }

  async removeProtectionProduct(productId, checkbox) {
    // Show loading state (protection control + all cart lines)
    this.setLoadingState(true);
    window.setCartLinesLoading(true);

    // Prevent the auto-check handler from re-adding it
    if (autoCheckProtectionHandler) {
      autoCheckProtectionHandler.isChecking = true;
      autoCheckProtectionHandler.userRemovedProtection = true; // ADD THIS LINE
    }

    // Persist across page reloads
    sessionStorage.setItem('protectionUserRemoved', 'true');

    try {
      // Resolve the protection line by vendor — its variant can be any price tier.
      let removeId = productId;
      try {
        const cart = await (await fetch('/cart.js')).json();
        const line = window.SavedByTiers.lineItem(cart);
        if (line) removeId = line.variant_id || line.id;
      } catch (e) {
        /* fall back to whatever was passed */
      }
      if (!removeId) {
        // Same leak as the add path: clear the cart-line loading state too.
        this.setLoadingState(false);
        window.setCartLinesLoading(false);
        return;
      }

      // Use /cart/update.js (updates map) — SavedBy monkeypatches fetch and rejects a
      // /cart/change.js removal of the protection line with a 400.
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: { [removeId]: 0 },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();

      await this.updateCartDisplay();
    } catch (error) {
      console.error('Error removing protection product:', error);
      checkbox.checked = true;
      // Revert the flag on error so auto-add still works
      if (autoCheckProtectionHandler) {
        autoCheckProtectionHandler.userRemovedProtection = false;
      }
      // Revert persisted flag too
      sessionStorage.removeItem('protectionUserRemoved');
      this.showError('Failed to remove protection product. Please try again.');
    } finally {
      setTimeout(() => {
        if (autoCheckProtectionHandler) {
          autoCheckProtectionHandler.isChecking = false;
          // NOTE: Do NOT reset userRemovedProtection here — that's the whole point
        }
        // Hide loading state
        this.setLoadingState(false);
        window.setCartLinesLoading(false);
      }, 1500);
    }
  }

  async updateCartDisplay() {
    try {
      // Fetch updated cart data
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Update cart drawer items
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      // Update cart icon bubble
      const cartIconBubble = document.querySelector('#cart-icon-bubble');
      const newCartIconBubble = html.querySelector('#cart-icon-bubble');
      if (cartIconBubble && newCartIconBubble) {
        cartIconBubble.replaceWith(newCartIconBubble);
      }

      // Reinitialize atelier slider after update
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.initAtelierSlider) {
        setTimeout(() => {
          cartDrawer.initAtelierSlider();
        }, 100);
      }
    } catch (error) {
      console.error('Failed to update cart display:', error);
      // Fallback: trigger a cart update event
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && cartDrawerItems.onCartUpdate) {
        cartDrawerItems.onCartUpdate();
      }
    }
  }

  showError(message) {
    const errorContainer = document.getElementById('CartDrawer-CartErrors');
    if (errorContainer) {
      errorContainer.textContent = message;
      setTimeout(() => {
        errorContainer.textContent = '';
      }, 3000);
    }
  }
}

class CollectionAddToCartHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleAddToCart.bind(this));
  }

  async handleAddToCart(event) {
    const btn = event.target.closest('.cart-btn-coll-add');
    if (!btn) return;

    event.preventDefault();

    btn.innerHTML = 'ADDING...';
    btn.setAttribute('disabled', true);

    const productId = btn.getAttribute('data-id');
    if (!productId) return;

    // Show the cart-line loader while the cart updates.
    window.setCartLinesLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: productId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      btn.innerHTML = 'ADD';
      btn.removeAttribute('disabled');

      // Put protection on the right tier BEFORE publishing, i.e. before the drawer
      // re-renders. Correcting it afterwards is too late: the re-render for this add is
      // requested from the pre-swap cart, so it lands last and repaints the protection
      // row with the old price. The cart is then right while the drawer shows the old
      // amount until something forces a full re-render (which is why unchecking and
      // re-checking the box "fixes" it, and why adding from a product page looks fine —
      // there the drawer renders after the swap has already settled).
      try {
        const preRender = await (await fetch('/cart.js')).json();
        const line = window.SavedByTiers.lineItem(preRender);
        const correct = window.SavedByTiers.tierFor(preRender);
        if (line && correct && String(line.variant_id || line.id) !== String(correct.variantId)) {
          await fetch('/cart/update.js', {
            ...fetchConfig(),
            body: JSON.stringify({
              updates: { [line.variant_id || line.id]: 0, [correct.variantId]: 1 },
            }),
          });
        }
      } catch (e) {
        /* non-fatal — the periodic check still corrects the cart, just a beat later */
      }

      // Publish cart update event so auto-check handler can catch it
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // Fetch updated cart to get full cart data
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'collection-add-to-cart',
            productVariantId: productId,
            cartData: cartData,
          });
        } else {
          // Fallback: publish with just the add response
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'collection-add-to-cart',
            productVariantId: productId,
            cartData: data,
          });
        }
      }

      // Update cart drawer and reinitialize atelier slider
      await this.updateCartDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      window.setCartLinesLoading(false);
    }
  }

  async updateCartDrawer() {
    try {
      const response = await fetch(window.location.href);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get fresh cart-drawer from response
      const newCartDrawer = doc.querySelector('cart-drawer');
      const currentCartDrawer = document.querySelector('cart-drawer');

      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;

        // Reinitialize atelier slider after cart drawer update
        if (currentCartDrawer.initAtelierSlider) {
          setTimeout(() => {
            currentCartDrawer.initAtelierSlider();
          }, 100);
        }
      }

      // Update cart icon bubble
      const newCartIconBubble = doc.querySelector('#cart-icon-bubble');
      const currentCartIconBubble = document.querySelector('#cart-icon-bubble');
      if (newCartIconBubble && currentCartIconBubble) {
        currentCartIconBubble.replaceWith(newCartIconBubble);
      }
    } catch (error) {
      console.error('Failed to reload cart drawer:', error);
    }
  }
}

// Function to open SavedBy package protection popup
function openSavedByPopup() {
  try {
    // First, check if the SavedBy widget exists
    const savedByWidget = document.querySelector('savedby-checkout-plus');

    if (!savedByWidget) {
      console.warn('SavedBy widget not found on the page');
      return false;
    }

    // Wait for the widget to be fully loaded if shadow root is not available yet
    if (!savedByWidget.shadowRoot) {
      console.warn('SavedBy widget shadow root not accessible, waiting for widget to load...');

      // Try to wait for the widget to load
      return new Promise((resolve) => {
        const checkShadowRoot = () => {
          if (savedByWidget.shadowRoot) {
            const result = openSavedByPopup();
            resolve(result);
          } else {
            setTimeout(checkShadowRoot, 100);
          }
        };
        checkShadowRoot();
      });
    }

    // Find the info help button in the shadow DOM
    const infoButton = savedByWidget.shadowRoot.querySelector('.sb__info-help-button');

    if (!infoButton) {
      console.warn('SavedBy info button not found in shadow DOM');
      return false;
    }

    // Click the button to open the popup
    infoButton.click();
    return true;
  } catch (error) {
    console.error('Error opening SavedBy popup:', error);
    return false;
  }
}

// Enhanced function to handle custom info icon clicks
async function handleCustomInfoIconClick(event) {
  // Check if the clicked element is an info icon
  const infoIcon = event.target.closest('.info-icon');

  if (!infoIcon) return;

  // // Prevent default behavior and stop event propagation to prevent other handlers
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  // Try to open the SavedBy popup
  await openSavedByPopup();
}

// Auto-check protection on cart updates handler
class AutoCheckProtectionHandler {
  constructor() {
    this.protectionProductVariantId = null;
    this.isChecking = false;
    this.userRemovedProtection = sessionStorage.getItem('protectionUserRemoved') === 'true';
    this.cartUpdateUnsubscriber = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready and get protection product ID
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get protection product variant ID from window (set in cart-drawer.liquid)
    if (window.protectionProductVariantId) {
      this.protectionProductVariantId = window.protectionProductVariantId.toString();

      // Subscribe to cart update events
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.handleCartUpdate(event);
        });
      }

      this.observeCartDrawer();
      this.recheckWhenTiersReady();
    }
  }

  /*
   * SavedBy populates window.SavedBy.feeTiers asynchronously — measured at several seconds
   * after DOMContentLoaded on the live store. Anything added in that window has no tier to
   * bracket against, and every later check is a no-op because syncProtectionTier returns
   * early while tierFor() is null. Re-check once the table actually arrives.
   */
  recheckWhenTiersReady() {
    if (window.SavedByTiers.ready()) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (window.SavedByTiers.ready()) {
        clearInterval(timer);
        this.checkAndAddProtection();
      } else if (attempts >= 120) {
        clearInterval(timer); // ~60s, then stop rather than poll forever
      }
    }, 500);
  }

  async observeCartDrawer() {
    // Watch for cart drawer updates as a fallback
    // This ensures protection is checked even if cart update events don't fire
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      let timeoutId = null;

      // Use MutationObserver to detect when cart drawer content changes
      const observer = new MutationObserver(() => {
        // Debounce the check to avoid excessive calls
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          this.checkAndAddProtection();
        }, 500);
      });

      const response = await fetch('/cart.js');
      if (!response.ok) {
        return;
      }

      observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
      });
    }
  }

  async handleCartUpdate(event) {
    // Prevent infinite loop - don't check if we're already checking
    if (this.isChecking) {
      return;
    }

    if (event.productVariantId && window.SavedByTiers.isProtectionVariant(event.productVariantId)) {
      if (event.cartData && event.cartData.item_count === 1) {
        setTimeout(() => {
          this.checkAndAddProtection();
        }, 200);
      }
      return;
    }

    if (event.source === 'cart-items' && event.cartData && event.cartData.item_count === 0) {
      // Cart is empty, don't add protection
      sessionStorage.removeItem('protectionUserRemoved');
      this.userRemovedProtection = false;
      return;
    }

    if (event.cartData && event.cartData.item_count > 0) {
      // Small delay to ensure cart is fully updated
      setTimeout(() => {
        this.checkAndAddProtection();
      }, 200);
    }
  }

  async checkAndAddProtection() {
    // Prevent infinite loop
    if (this.isChecking) return;

    // Fetch current cart state BEFORE honoring the "user removed" flag, so an empty cart
    // can reset it — otherwise a single uncheck would suppress auto-add for the session.
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        return;
      }

      const cartData = await response.json();

      // Keep the displayed protection price in sync with the current tier / line.
      this.updateProtectionPriceDisplay(cartData);

      // Empty cart = fresh start: forget any prior manual uncheck so the next product
      // added re-enables auto-add protection.
      if (cartData.item_count === 0) {
        this.userRemovedProtection = false;
        sessionStorage.removeItem('protectionUserRemoved');
        return;
      }

      // Cart has items: respect a manual uncheck — don't re-add protection.
      if (this.userRemovedProtection) {
        return;
      }

      // Identify the protection line by vendor — its variant can be any price tier.
      const protectionLine = window.SavedByTiers.lineItem(cartData);
      const hasProtection = !!protectionLine;

      // Keep the tickbox honest about the cart. The drawer re-renders as soon as the
      // product lands, which is before the protection add finishes, so Liquid paints the
      // box unticked and nothing put it right afterwards — protection was in the cart and
      // charged for while the control claimed it was off. Reached only when auto-add is
      // active: a manual uncheck returns above, so this cannot fight the shopper.
      const protectionCheckbox = document.querySelector('.protection-checkbox');
      if (protectionCheckbox && protectionCheckbox.checked !== hasProtection) {
        protectionCheckbox.checked = hasProtection;
      }

      // Protection with nothing shippable to cover → remove it by variant id (not a
      // line-indexed button, whose data-index may be stale after concurrent changes and
      // would 422). Covers protection sitting alone, and protection left with only a gift
      // card or other non-shipped item, which it cannot insure.
      if (hasProtection && !window.SavedByTiers.hasInsurableItem(cartData)) {
        try {
          // /cart/update.js (updates map) — SavedBy's patched fetch 400s a /cart/change removal.
          await fetch('/cart/update.js', {
            ...fetchConfig(),
            body: JSON.stringify({ updates: { [protectionLine.variant_id || protectionLine.id]: 0 } }),
          });
        } catch (e) {
          /* non-fatal */
        }
        const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
        if (cartItems && cartItems.onCartUpdate) cartItems.onCartUpdate();
        return;
      }

      // Protection already present → keep it on the correct tier for the current cart value.
      if (hasProtection) {
        await this.syncProtectionTier(cartData, protectionLine);
        return;
      }

      // No protection but something shippable present → opt in (correct tier variant).
      // A gift-card-only cart is skipped: nothing here is shipped, so nothing to insure.
      if (!hasProtection && window.SavedByTiers.hasInsurableItem(cartData)) {
        this.clickProtectionLabel();
      }
    } catch (error) {
      console.error('Error checking cart for protection product:', error);
    }
  }

  // Show the protection price: the actual line price when added, else the current tier price.
  updateProtectionPriceDisplay(cartData) {
    const el = document.querySelector('.cart-package-protection-price');
    if (!el) return;

    // Nothing shippable in the cart (e.g. a gift card on its own): protection cannot apply,
    // so showing a tier price is misleading — tierFor() still returns the cheapest bracket
    // for a zero subtotal, which is where the stray "+$1.47" came from. Say why instead.
    const itemCount = ((cartData && cartData.items) || []).length;
    if (itemCount > 0 && !window.SavedByTiers.hasInsurableItem(cartData)) {
      el.textContent = 'Not available for gift cards';
      return;
    }

    const line = window.SavedByTiers.lineItem(cartData);
    let cents = null;
    if (line) {
      cents = line.final_price;
    } else {
      const tier = window.SavedByTiers.tierFor(cartData);
      if (tier) cents = Math.round(tier.price * 100);
    }
    if (cents == null) return;
    let text;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      text = Shopify.formatMoney(
        cents,
        window.moneyFormat || (window.Shopify && Shopify.money_format) || '${{amount}}'
      );
    } else {
      text = '$' + (cents / 100).toFixed(2);
    }
    el.textContent = '+' + text;
  }

  // Swap the protection line to the correct price tier when the cart value moves brackets.
  async syncProtectionTier(cartData, protectionLine) {
    if (this.isChecking) return;
    const tier = window.SavedByTiers.tierFor(cartData);
    if (!tier) return;
    const currentId = String(protectionLine.variant_id || protectionLine.id);
    if (currentId === String(tier.variantId)) return; // already the correct tier

    this.isChecking = true;
    window.setCartLinesLoading(true);
    try {
      await fetch('/cart/update.js', {
        ...fetchConfig(),
        body: JSON.stringify({ updates: { [currentId]: 0, [tier.variantId]: 1 } }),
      });
      const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartItems && cartItems.onCartUpdate) cartItems.onCartUpdate();
    } catch (e) {
      /* non-fatal */
    } finally {
      setTimeout(() => {
        this.isChecking = false;
        window.setCartLinesLoading(false);
      }, 500);
    }
  }

  clickProtectionLabel() {
    if (this.isChecking) {
      return;
    }

    this.isChecking = true;

    // Find the protection-label element
    const protectionLabel = document.querySelector('.protection-label');

    if (!protectionLabel) {
      this.isChecking = false;
      return;
    }

    // Check if checkbox is already checked (don't click if already checked)
    const protectionCheckbox = document.querySelector('.protection-checkbox');
    if (protectionCheckbox && protectionCheckbox.checked) {
      this.isChecking = false;
      return;
    }

    // Create and dispatch a click event on the protection-label
    // This will trigger ProtectionProductHandler.handleProtectionClick
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });

    // Click the protection-label which will be handled by ProtectionProductHandler
    protectionLabel.dispatchEvent(clickEvent);

    // Reset flag after a delay
    setTimeout(() => {
      this.isChecking = false;
    }, 1000);
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
  }
}

// Initialize protection product handler
let protectionHandler = null;
let collectionAddToCartHandler = null;
let autoCheckProtectionHandler = null;

document.addEventListener('DOMContentLoaded', () => {
  if (!protectionHandler) {
    protectionHandler = new ProtectionProductHandler();
  }
  if (!collectionAddToCartHandler) {
    collectionAddToCartHandler = new CollectionAddToCartHandler();
  }

  // Initialize auto-check protection handler
  if (!autoCheckProtectionHandler) {
    autoCheckProtectionHandler = new AutoCheckProtectionHandler();
  }

  // Add event listener for custom info icon clicks with capture to ensure it runs first
  document.addEventListener('click', handleCustomInfoIconClick, true);
});

// Also initialize when cart drawer is opened (in case DOM changes)
if (typeof window !== 'undefined') {
  window.initProtectionHandler = () => {
    if (!protectionHandler) {
      protectionHandler = new ProtectionProductHandler();
    }
  };

  window.initCollectionAddToCartHandler = () => {
    if (!collectionAddToCartHandler) {
      collectionAddToCartHandler = new CollectionAddToCartHandler();
    }
  };

  // Reinitialize auto-check handler when cart drawer updates
  window.initAutoCheckProtectionHandler = () => {
    if (!autoCheckProtectionHandler && window.protectionProductVariantId) {
      autoCheckProtectionHandler = new AutoCheckProtectionHandler();
    } else if (
      autoCheckProtectionHandler &&
      !autoCheckProtectionHandler.protectionProductVariantId &&
      window.protectionProductVariantId
    ) {
      // Update protection product ID if it wasn't set before and re-setup
      autoCheckProtectionHandler.protectionProductVariantId = window.protectionProductVariantId.toString();
      autoCheckProtectionHandler.setup();
    }
  };

  // Make the function globally available
  window.openSavedByPopup = openSavedByPopup;
}
